import { Link } from "react-router-dom";
import { SectionHeader } from "../components/UI";
import {
  ArrowLeft, Plug, Slack, Github, Mail, MessageCircle, Cloud, KeyRound,
  Building2, ShieldCheck, CheckCircle2,
} from "lucide-react";

/*
 * Settings , Integrations
 * Directory of connectors. Most are "Coming soon" placeholders,
 * but the layout mirrors HubSpot's integrations catalog.
 */

const CATEGORIES = [
  {
    name: "Chat & Messaging",
    items: [
      { name: "Slack", icon: Slack, status: "coming_soon", body: "Route new-case pings and findings approvals to a Slack channel." },
      { name: "Microsoft Teams", icon: MessageCircle, status: "coming_soon", body: "Send notifications to Teams channels or DMs." },
      { name: "Discord", icon: MessageCircle, status: "coming_soon", body: "For workspaces that live in Discord." },
    ],
  },
  {
    name: "Email & Calendar",
    items: [
      { name: "Resend", icon: Mail, status: "coming_soon", body: "Transactional email delivery for PropHR notifications." },
      { name: "SendGrid", icon: Mail, status: "coming_soon", body: "Alternate email delivery provider." },
      { name: "Google Calendar", icon: Cloud, status: "coming_soon", body: "Auto-schedule fact-finding meetings from case detail." },
    ],
  },
  {
    name: "Identity & SSO",
    items: [
      { name: "Google Workspace", icon: KeyRound, status: "beta", body: "SSO + directory sync. Buttons on login are wired , IT hookup pending." },
      { name: "Microsoft 365", icon: KeyRound, status: "beta", body: "Enterprise SSO through Entra ID." },
      { name: "Okta", icon: KeyRound, status: "coming_soon", body: "SAML/SCIM for large orgs." },
    ],
  },
  {
    name: "HRIS & People systems",
    items: [
      { name: "Workday", icon: Building2, status: "coming_soon", body: "Sync org chart and status changes." },
      { name: "BambooHR", icon: Building2, status: "coming_soon", body: "Directory + department sync." },
      { name: "Rippling", icon: Building2, status: "coming_soon", body: "Employee lifecycle events." },
    ],
  },
  {
    name: "Ticketing & DevOps",
    items: [
      { name: "Jira", icon: Github, status: "coming_soon", body: "Track investigation subtasks in Jira." },
      { name: "Zendesk", icon: Github, status: "coming_soon", body: "Sync tickets with your service cloud." },
    ],
  },
];

const STATUS_META = {
  beta: { label: "Beta", pill: "bg-blue-50 text-blue-700 border-blue-200" },
  coming_soon: { label: "Coming soon", pill: "bg-slate-100 text-slate-700 border-slate-200" },
  live: { label: "Live", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function SettingsIntegrations() {
  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="settings-integrations">
      <Link to="/settings" className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-2">
        <ArrowLeft size={12}/> ALL SETTINGS
      </Link>
      <SectionHeader
        title="Integrations"
        subtitle="Connect PropHR to the tools your organization already runs on. Most connectors are on the near-term roadmap , tell your Company Admin which ones you need most."
        right={
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-slate-200 bg-slate-50 px-2 py-1">
            <Plug size={11} strokeWidth={2}/> Roadmap · 2026
          </span>
        }
      />

      <div className="border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-start gap-3">
        <ShieldCheck size={16} className="text-emerald-700 mt-0.5 shrink-0"/>
        <div className="text-xs text-emerald-900 leading-relaxed">
          <div className="font-semibold">Trust is not optional.</div>
          Every integration is opt-in, permission-scoped, and reversible. Data flows are logged in the audit trail.
        </div>
      </div>

      <div className="space-y-10">
        {CATEGORIES.map((cat) => (
          <section key={cat.name}>
            <div className="eyebrow mb-3">{cat.name}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.items.map((it) => {
                const meta = STATUS_META[it.status] || STATUS_META.coming_soon;
                return (
                  <div
                    key={it.name}
                    className="border border-slate-200 bg-white p-5 flex flex-col"
                    data-testid={`integration-${it.name.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 border border-slate-200 bg-slate-50 flex items-center justify-center">
                        <it.icon size={16} strokeWidth={1.75} className="text-slate-700"/>
                      </div>
                      <span className={"text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 " + meta.pill}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="font-display text-base font-semibold text-slate-900">{it.name}</div>
                    <div className="text-xs text-slate-500 mt-1 leading-relaxed flex-1">{it.body}</div>
                    <button
                      disabled
                      className="mt-4 text-xs text-slate-500 border border-slate-200 py-1.5 px-3 self-start cursor-not-allowed"
                      title="Coming soon"
                    >
                      Notify me
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-10 border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 size={14} strokeWidth={2} className="text-emerald-700"/>
          <div className="font-display text-base font-semibold text-slate-900">Need something specific?</div>
        </div>
        <div className="text-sm text-slate-600 leading-relaxed">
          File a request via the <Link to="/tickets" className="underline hover:text-slate-900">support desk</Link> , the top integrations by customer request ship first.
        </div>
      </div>
    </div>
  );
}
